"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/* eslint-disable no-param-reassign */
const three_1 = require("three");
const Viewer_1 = __importDefault(require("../Viewer"));
const classes_1 = require("../classes");
const utils_3d_1 = require("./utils.3d");
const consts_1 = require("../consts");
const _1 = require(".");
const BLOB_TYPE = 'image/jpeg';
// eslint-disable-next-line no-shadow
var ExplodedScreenShotTypes;
(function (ExplodedScreenShotTypes) {
    ExplodedScreenShotTypes["explodedWithBalloonsForExtrusions"] = "explodedWithBalloonsForExtrusions";
    ExplodedScreenShotTypes["explodedWithBalloonsForComponents"] = "explodedWithBalloonsForComponents";
})(ExplodedScreenShotTypes || (ExplodedScreenShotTypes = {}));
// eslint-disable-next-line no-shadow
var IndividualComponentScreenShotTypes;
(function (IndividualComponentScreenShotTypes) {
    IndividualComponentScreenShotTypes["panels"] = "panels";
})(IndividualComponentScreenShotTypes || (IndividualComponentScreenShotTypes = {}));
// eslint-disable-next-line no-shadow
var BaseScreenShotTypes;
(function (BaseScreenShotTypes) {
    BaseScreenShotTypes["isometric"] = "isometric";
    BaseScreenShotTypes["front"] = "front";
    BaseScreenShotTypes["isometricWithBalloons"] = "isometricWithBalloons";
})(BaseScreenShotTypes || (BaseScreenShotTypes = {}));
// eslint-disable-next-line no-shadow
var GroupedScreenShotTypes;
(function (GroupedScreenShotTypes) {
    GroupedScreenShotTypes["projectDimensions"] = "projectDimensions";
})(GroupedScreenShotTypes || (GroupedScreenShotTypes = {}));
function takeFrontScreenShot(renderer, boundingBox, camera, scene) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve) => {
            const boxSize = new three_1.Vector3();
            boundingBox.getSize(boxSize);
            const boxCenter = new three_1.Vector3();
            boundingBox.getCenter(boxCenter);
            const maxSide = Math.max(boxSize.x, boxSize.z);
            const distance = (maxSide * consts_1.consts.ZOOM_TO_FIT_ADDITIONAL_SIZE_COEF) /
                2 /
                Math.tan(((camera.getEffectiveFOV() / 2) * Math.PI) / 180);
            camera.position.copy(boxCenter.clone().add(new three_1.Vector3(0, distance + boxSize.y / 2, 0)));
            camera.lookAt(boxCenter);
            camera.updateProjectionMatrix();
            renderer.render(scene, camera);
            renderer.domElement.toBlob(resolve, BLOB_TYPE);
        });
    });
}
function setCameraPositionForIsometricScreenShot(camera, boundingBox) {
    const boundingSphere = boundingBox.getBoundingSphere(new three_1.Sphere());
    const boxCenter = boundingBox.getCenter(new three_1.Vector3());
    const fovHalfRadians = ((camera.getEffectiveFOV() / 2) * Math.PI) / 180;
    const distance = (consts_1.consts.ZOOM_TO_FIT_ADDITIONAL_SIZE_COEF * boundingSphere.radius) / Math.tan(fovHalfRadians);
    camera.position.copy(boxCenter.clone().add(new three_1.Vector3().copy(consts_1.consts.SCREENSHOT_CAMERA_DIRECTION).normalize().multiplyScalar(distance)));
    camera.lookAt(boxCenter);
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld();
}
function takeIsometricScreenShot(renderer, boundingBox, camera, scene) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve) => {
            setCameraPositionForIsometricScreenShot(camera, boundingBox);
            renderer.render(scene, camera);
            renderer.domElement.toBlob(resolve, BLOB_TYPE, 1);
        });
    });
}
function takeIsometricScreenShotWithBomBalloons(renderer, boundingBox, camera, scene, bomBalloonInclusions) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve) => {
            setCameraPositionForIsometricScreenShot(camera, boundingBox);
            Viewer_1.default.instance.drawBalloons(camera, bomBalloonInclusions);
            renderer.render(scene, camera);
            Viewer_1.default.instance.clearBalloons();
            renderer.domElement.toBlob(resolve, BLOB_TYPE, 1);
        });
    });
}
function takeExplodedScreenshots(renderer, camera, scene, boundingBox, requestedScreenShots, explosionCoefficient) {
    return __awaiter(this, void 0, void 0, function* () {
        const explodedScreenshots = {};
        const extrusionProfiles = Viewer_1.default.instance.getExtrusionProfiles();
        const panels = Viewer_1.default.instance.getPanels();
        const components = Viewer_1.default.instance.getComponents();
        const nonFastenerComponents = components.filter((component) => !_1.Utils.isFastener(component));
        // Combine the items to explode
        const itemsToExplode = [...panels, ...extrusionProfiles, ...nonFastenerComponents];
        if (itemsToExplode.length <= 1) {
            return explodedScreenshots;
        }
        scene.add(camera);
        // Filter the scene objects to remove fasteners
        const fasteners = Viewer_1.default.instance.getFasteners();
        fasteners.forEach((fastener) => {
            fastener.visible = false;
        });
        // Decrease panel opacity by 50%
        const originalPanelSettings = [];
        panels.forEach((panel) => {
            if (!Array.isArray(panel.mesh.material)) {
                originalPanelSettings.push({
                    panel,
                    opacity: panel.mesh.material.opacity,
                    transparent: panel.mesh.material.transparent,
                });
            }
            panel.setTransparency(true, consts_1.consts.SCREENSHOT_EXPLODED_VIEW_PANEL_OPACITY);
        });
        // Disable children translation for the extrusion profiles
        const originalExtrusionSettings = [];
        extrusionProfiles.forEach((extrusionProfile) => {
            var _a;
            originalExtrusionSettings.push({
                extrusionProfile,
                noMoveJoinedComps: (_a = extrusionProfile.userData.noMoveJoinedComps) !== null && _a !== void 0 ? _a : null,
            });
            extrusionProfile.userData.noMoveJoinedComps = true;
        });
        // Keep the original tranformations stored to reset after taking screenshot
        const originalItemTransforms = itemsToExplode.map((item) => ({
            item,
            position: item.position.clone(),
        }));
        // Calculate the translation direction vectors for each part on the assembly, scale it by the decided factor and apply it on the corresponding part to translate it to get the exploded view.
        const explosionCenter = boundingBox.getCenter(new three_1.Vector3());
        // Calculate the bounding box of the updated assembly and use it for calling takeIsometricScreenShotWithBomBalloons().
        const explodedBoundingBox = new three_1.Box3();
        const explosionOffset = explosionCoefficient && explosionCoefficient >= 1 ? explosionCoefficient : consts_1.consts.SCREENSHOT_EXPLODED_VIEW_OFFSET;
        itemsToExplode.forEach((item) => {
            const itemCentroid = _1.Utils.getAABBoxCenter(item, _1.Utils.isPanel(item));
            const explosionDirection = new three_1.Vector3().subVectors(itemCentroid, explosionCenter);
            item.translate(explosionDirection.clone().negate());
            item.translate(explosionDirection.clone().multiplyScalar(explosionOffset));
            explodedBoundingBox.expandByObject(item);
        });
        // Create two screenshots one with BOM Balloons for the extrusion profiles only and other with BOM Balloons for components only.
        // Take screenshot with BOM Balloons for the extrusion profiles if any extrusion profiles exist
        if ((requestedScreenShots === null || requestedScreenShots === void 0 ? void 0 : requestedScreenShots.explodedWithBalloonsForExtrusions) && extrusionProfiles.length) {
            const bomBalloonInclusions = {
                EXTRUSION: true,
            };
            const explodedScreenshotWithAnnotatedExtrusionProfiles = yield takeIsometricScreenShotWithBomBalloons(renderer, explodedBoundingBox, camera, scene, bomBalloonInclusions);
            explodedScreenshots.explodedWithBalloonsForExtrusions = explodedScreenshotWithAnnotatedExtrusionProfiles;
        }
        // Take screenshot with BOM Balloons for the components if any component exist
        const isExploadableComponentPresent = panels.length || nonFastenerComponents.length;
        if ((requestedScreenShots === null || requestedScreenShots === void 0 ? void 0 : requestedScreenShots.explodedWithBalloonsForComponents) && isExploadableComponentPresent) {
            const bomBalloonInclusions = {
                COMPONENT: true,
            };
            const explodedScreenshotWithAnnotatedComponents = yield takeIsometricScreenShotWithBomBalloons(renderer, explodedBoundingBox, camera, scene, bomBalloonInclusions);
            explodedScreenshots.explodedWithBalloonsForComponents = explodedScreenshotWithAnnotatedComponents;
        }
        // Reset the items to their original settings
        originalItemTransforms.forEach(({ item, position }) => {
            item.position.copy(position);
            item.updateMatrix();
        });
        originalExtrusionSettings.forEach(({ extrusionProfile, noMoveJoinedComps }) => {
            if (noMoveJoinedComps) {
                extrusionProfile.userData.noMoveJoinedComps = noMoveJoinedComps;
            }
            else {
                delete extrusionProfile.userData.noMoveJoinedComps;
            }
        });
        originalPanelSettings.forEach(({ panel, opacity, transparent }) => {
            panel.setTransparency(transparent, opacity);
        });
        fasteners.forEach((fastener) => {
            fastener.visible = true;
        });
        scene.remove(camera);
        return explodedScreenshots;
    });
}
function takeScreenShots(viewer, size, requestedScreenShots, explosionCoefficient) {
    return __awaiter(this, void 0, void 0, function* () {
        const canvasCopy = document.createElement('canvas');
        const hiddenObjects = [];
        const unselectedObjects = [];
        const objectsWithChangedOpacity = [];
        const rendererCopy = new three_1.WebGLRenderer({
            antialias: true,
            canvas: canvasCopy,
            preserveDrawingBuffer: true,
        });
        const boundingBox = new three_1.Box3();
        viewer.toggleAnimationMode();
        const scene = viewer.getScene();
        scene.children.forEach((obj) => {
            var _a, _b;
            const objIsConnectionWithoutFasteners = (0, utils_3d_1.isConnection)(obj) && obj.getFasteners().length === 0;
            if (obj instanceof classes_1.BaseObject3D &&
                obj.isCustomObject &&
                obj.uuid !== ((_a = viewer.getDesignProMain().scalingTool.getObject()) === null || _a === void 0 ? void 0 : _a.uuid) &&
                obj.uuid !== ((_b = viewer.getDesignProMain().snappingTool.getObject()) === null || _b === void 0 ? void 0 : _b.uuid)) {
                if (viewer.getDesignProMain().selectionTool.selectedObjectsUuids.includes(obj.uuid)) {
                    obj.unselect();
                    unselectedObjects.push(obj);
                }
                if (!Array.isArray(obj.mesh.material) && obj.mesh.material.opacity !== 1.0) {
                    objectsWithChangedOpacity.push({ object: obj, prevOpacity: obj.mesh.material.opacity });
                    obj.mesh.material.setValues({ opacity: 1.0 });
                }
                boundingBox.expandByObject(obj);
            }
            else if (obj.visible &&
                objIsConnectionWithoutFasteners &&
                !(obj instanceof three_1.Light) &&
                obj.uuid !== viewer.getGrid().uuid) {
                obj.visible = false;
                hiddenObjects.push(obj);
            }
        });
        viewer.getDesignProMain().highlightingTool.clearConnectionHighlight();
        rendererCopy.setSize(size !== null && size !== void 0 ? size : consts_1.consts.SCREENSHOT_SIDE_LENGTH_PX, size !== null && size !== void 0 ? size : consts_1.consts.SCREENSHOT_SIDE_LENGTH_PX);
        const orthographicCamera = new three_1.OrthographicCamera(-1, 1, 1, -1, 0.1, 500000);
        orthographicCamera.up.set(0, 0, -1);
        const currentCamera = viewer.getCamera();
        const perspectiveCamera = new three_1.PerspectiveCamera(currentCamera.fov, 1, currentCamera.near, currentCamera.far);
        perspectiveCamera.up.copy(currentCamera.up);
        perspectiveCamera.zoom = currentCamera.zoom;
        const screenShots = {};
        if (requestedScreenShots === null || requestedScreenShots === void 0 ? void 0 : requestedScreenShots.front) {
            const front = yield takeFrontScreenShot(rendererCopy, boundingBox, perspectiveCamera, scene);
            screenShots.front = front;
        }
        if (requestedScreenShots === null || requestedScreenShots === void 0 ? void 0 : requestedScreenShots.isometric) {
            const isometric = yield takeIsometricScreenShot(rendererCopy, boundingBox, perspectiveCamera, scene);
            screenShots.isometric = isometric;
        }
        if (requestedScreenShots === null || requestedScreenShots === void 0 ? void 0 : requestedScreenShots.isometricWithBalloons) {
            scene.add(perspectiveCamera);
            const withBalloons = yield takeIsometricScreenShotWithBomBalloons(rendererCopy, boundingBox, perspectiveCamera, scene);
            scene.remove(perspectiveCamera);
            screenShots.isometricWithBalloons = withBalloons;
        }
        if (Object.keys(ExplodedScreenShotTypes).some((key) => requestedScreenShots === null || requestedScreenShots === void 0 ? void 0 : requestedScreenShots[key])) {
            const explodedScreenShots = yield takeExplodedScreenshots(rendererCopy, perspectiveCamera, scene, boundingBox, requestedScreenShots, explosionCoefficient);
            // eslint-disable-next-line no-restricted-syntax
            for (const key of Object.keys(explodedScreenShots)) {
                screenShots[key] = explodedScreenShots[key];
            }
        }
        if (requestedScreenShots === null || requestedScreenShots === void 0 ? void 0 : requestedScreenShots.panels) {
            const panelScreenShots = yield Viewer_1.default.instance.takePanelScreenShots();
            screenShots.panels = panelScreenShots;
        }
        if (requestedScreenShots === null || requestedScreenShots === void 0 ? void 0 : requestedScreenShots.projectDimensions) {
            const projectDimensionsScreenShots = yield Viewer_1.default.instance.takeProjectsDimensionsScreenShots();
            screenShots.projectDimensions = projectDimensionsScreenShots;
        }
        // eslint-disable-next-line no-return-assign, no-param-reassign
        hiddenObjects.forEach((obj) => (obj.visible = true));
        unselectedObjects.forEach((obj) => obj.select());
        objectsWithChangedOpacity.forEach(({ object, prevOpacity }) => object.mesh.material.setValues({ opacity: prevOpacity }));
        viewer.toggleAnimationMode();
        canvasCopy.remove();
        rendererCopy.dispose();
        rendererCopy.forceContextLoss();
        return screenShots;
    });
}
exports.default = takeScreenShots;
function takeIsometricScreenShotOld(renderer, boundingBox, camera, scene) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve) => {
            const boundingSphere = new three_1.Sphere();
            boundingBox.getBoundingSphere(boundingSphere);
            const boxCenter = new three_1.Vector3();
            boundingBox.getCenter(boxCenter);
            camera.position.copy(boxCenter
                .clone()
                .add(new three_1.Vector3().copy(consts_1.consts.SCREENSHOT_CAMERA_DIRECTION).multiplyScalar(boundingSphere.radius)));
            camera.lookAt(boxCenter);
            camera.zoom = (camera.right - camera.left) / (boundingSphere.radius * 2 * consts_1.consts.ZOOM_TO_FIT_ADDITIONAL_SIZE_COEF);
            camera.updateProjectionMatrix();
            renderer.render(scene, camera);
            renderer.domElement.toBlob(resolve, BLOB_TYPE, 1);
        });
    });
}
